import React, { useState, useEffect } from 'react';
import { Card, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { PhoneInput } from '../components/ui/PhoneInput';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { isAdmin } from '../utils/roles';
import { apiUpdate } from '../api/apiClient';

export const Settings: React.FC = () => {
  const { user, updateUser } = useAuth();

  // Profile fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Admin / application fields
  const [timezone, setTimezone] = useState('America/New_York');

  // 2FA toggle — stored on the user record
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);

  // Feedback state
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileStatus, setProfileStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminStatus, setAdminStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  // Load user data from auth context on mount / when user changes
  useEffect(() => {
    if (user) {
      setName(user.name ?? [user.first_name, user.last_name].filter(Boolean).join(' '));
      setEmail(user.email ?? '');
      setPhone(user.phone ?? '');
      setTimezone((user as any).timezone ?? 'America/New_York');
      if ((user as any).two_factor_enabled != null) setTwoFaEnabled(!!(user as any).two_factor_enabled);
    }
  }, [user]);

  const saveProfile = async () => {
    if (!user) return;
    setProfileSaving(true);
    setProfileStatus(null);
    try {
      const parts = name.trim().split(/\s+/);
      const payload: Record<string, any> = {
        first_name: parts.shift() || '',
        last_name: parts.join(' '),
        email,
        phone,
      };

      await apiUpdate('users', user.uuid || user.id, payload);
      updateUser({ name, first_name: payload.first_name, last_name: payload.last_name, email, phone });
      setProfileStatus({ ok: true, msg: 'Profile saved successfully!' });
    } catch (err: any) {
      setProfileStatus({ ok: false, msg: err?.message ?? 'Failed to save profile.' });
    } finally {
      setProfileSaving(false);
    }
  };

  const saveAdminSettings = async () => {
    if (!user) return;
    setAdminSaving(true);
    setAdminStatus(null);
    try {
      await apiUpdate('users', (user as any).uuid || user.id, {
        two_factor_enabled: twoFaEnabled,
        timezone,
      });
      updateUser({ two_factor_enabled: twoFaEnabled, timezone });
      setAdminStatus({ ok: true, msg: 'Settings saved successfully!' });
    } catch (err: any) {
      setAdminStatus({ ok: false, msg: err?.message ?? 'Failed to save settings.' });
    } finally {
      setAdminSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-gray-500 mt-1">Manage your account and application settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Account / Profile ── */}
        <Card>
          <CardHeader title="Account Settings" subtitle="Update your personal information" />

          {profileStatus && (
            <div className={`text-sm p-3 rounded-lg mb-4 ${profileStatus.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {profileStatus.msg}
            </div>
          )}

          <Input label="Full Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <PhoneInput label="Phone" value={phone} onChange={setPhone} />

          <Button onClick={saveProfile} disabled={profileSaving}>
            {profileSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Card>

        {/* ── Admin / Application Settings ── */}
        {isAdmin(user?.role || 'customer') && (
          <Card>
            <CardHeader title="Application Settings" subtitle="Admin-only configuration" />

            {adminStatus && (
              <div className={`text-sm p-3 rounded-lg mb-4 ${adminStatus.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {adminStatus.msg}
              </div>
            )}

            <Input label="Default Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Two-Factor Authentication</label>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Require 2FA for all admin users</span>
                <button
                  type="button"
                  onClick={() => setTwoFaEnabled((v) => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${twoFaEnabled ? 'bg-brand-600' : 'bg-gray-300'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${twoFaEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>
            </div>

            <Button onClick={saveAdminSettings} disabled={adminSaving}>
              {adminSaving ? 'Saving…' : 'Save Settings'}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};
